import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase, type Item, type Store, type Trip } from '../lib/supabase'
import { replayRoute, walksOf } from '../lib/storeRoute'
import { boughtIn } from '../lib/trips'
import { itemKey } from '../lib/normalize'
import { TRIPS_KEY } from './useTrips'
import { itemHistoryKey } from './useItemHistory'
import { SHARED_TRIPS_KEY, STORES_KEY } from './useShopping'

type HistoryRow = Pick<Item, 'id' | 'name' | 'done' | 'done_at' | 'done_by' | 'deleted_at'>

/** What Stats' trip editor changes on a past trip (046). */
export type TripPatch = Partial<Pick<Trip, 'store_id' | 'picked_order' | 'deleted_at'>>

// Correcting a past trip from Stats: which store it was, the order it was
// walked in (picked_order), or deleting it (deleted_at, soft: its items stay
// in the history). Each is a plain update the trips policy already allows;
// the cached trips change at once and go back if the write fails.
//
// A store's route was learned trip by trip, so a corrected trip re-teaches
// it: the route is learned again from that store's trips in the history,
// oldest first (lib/storeRoute.ts replayRoute) — for the trip's store and,
// after a move, the one it left. Only Plus learns (Stats is Plus); best
// effort, like learning at Finish trip.
export function useTripEdits(householdId: string) {
  const qc = useQueryClient()

  // The history holds only the newer trips, so the route as it was keeps what
  // older ones taught (replayRoute's `old`); the edited trip's own items are
  // forgotten by a store that no longer counts it (moved away, or deleted).
  // No history loaded yet: nothing to learn from, so the routes stay as they are.
  const relearn = useCallback(async (edited: Trip, patch: TripPatch) => {
    const trips = qc.getQueryData<Trip[]>(TRIPS_KEY(householdId)) ?? []
    const rows = qc.getQueryData<HistoryRow[]>(itemHistoryKey(householdId))
    if (!rows || !edited.finished_at) return
    const stores = qc.getQueryData<Store[]>(STORES_KEY(householdId)) ?? []
    const editedKeys = boughtIn(edited.finished_at, rows).map(r => itemKey(r.name))
    const movedTo = patch.store_id !== undefined && patch.store_id !== edited.store_id ? patch.store_id : undefined
    for (const storeId of new Set([edited.store_id, movedTo])) {
      if (!storeId) continue
      const leaves = !!patch.deleted_at || (movedTo !== undefined && storeId === edited.store_id)
      const route = replayRoute(
        trips
          .filter(t => t.store_id === storeId && t.finished_at && !t.deleted_at)
          .map(t => ({ finishedAt: Date.parse(t.finished_at!), walks: walksOf(boughtIn(t.finished_at!, rows), t.picked_order) })),
        { route: stores.find(s => s.id === storeId)?.route ?? [], forget: leaves ? editedKeys : [] },
      )
      qc.setQueryData<Store[]>(STORES_KEY(householdId), old => (old ?? []).map(s => (s.id === storeId ? { ...s, route } : s)))
      await supabase.from('stores').update({ route }).eq('id', storeId)
    }
  }, [qc, householdId])

  /** Changes a past trip; resolves false when it couldn't (the trip is as it was). */
  const editTrip = useCallback(async (trip: Trip, patch: TripPatch): Promise<boolean> => {
    const key = TRIPS_KEY(householdId)
    const before = qc.getQueryData<Trip[]>(key)
    qc.setQueryData<Trip[]>(key, old => (old ?? []).map(t => (t.id === trip.id ? { ...t, ...patch } : t)))
    const { error } = await supabase.from('trips').update(patch).eq('id', trip.id)
    if (error) {
      qc.setQueryData(key, before)
      return false
    }
    void relearn(trip, patch)
    void qc.invalidateQueries({ queryKey: SHARED_TRIPS_KEY(householdId) })
    return true
  }, [qc, householdId, relearn])

  const deleteTrip = useCallback(
    (trip: Trip) => editTrip(trip, { deleted_at: new Date().toISOString() }),
    [editTrip],
  )

  return { editTrip, deleteTrip }
}
